/**
 * Created by Thomas Mark
 * Date: 2019-10-29
 * 
 * Container verwalten
 * 
 * In diesem ContainersController werden alle Kataloge eines Unternehmens in einer Übersicht angezeigt.
 * Der Benutzer kann hier Kataloge herunterladen, löschen, aktualisieren oder auswählen. 
 * Hat der Benutzer einen Katalog ausgewählt, so werden die Portfolios (Kacheln) mit Hilfe 
 * des PortfolioControllers angezeigt. Der PortfolioController lädt dann die Daten aus dem 
 * lokalen nichtflüchtigen Speicher (IndexedDB) in den lokalen flüchtigen Speicher (REDUX-Store).
 * 
 * Möchte eine Benutzer einen Katalog herunterladen oder aktualisieren, so muss er sich zuvor am 
 * Server anmelden.
 * 
 * Möchte der Benutzer einen Katalog löschen, so ist keine Anmeldung erforderlich, da hier lediglich
 * alle lokalen Daten aus dem nichtflüchtigen Speicher (IndexedDB) entfernt werden. Ein Katalog kann
 * nur über den SuperAdmin im Admin-Dialog physikalisch gelöscht werden.
 * 
 * Ist der Benutzer gleichzeitig ein Administrator, so kann dieser über das Icon 'Versions' in den Dialog 
 * zur Verwaltung der Katalog-Versionen wechseln (VersionsController)  
 * 
 */
import React from 'react';
import axios from "common/helpers/axios.helper"
import { Redirect } from "react-router-dom";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";

import { FooterItem, FooterItemCatalog } from "../../partials/FooterItem";
import Header from "../../partials/Header";
import Footer from "../../partials/Footer";
import { ConfirmModal } from "../../partials/ConfirmModal";
import ProgressBar from "../../partials/ProgressBar";
import ProgressView from "../../partials/ProgressView";
import DownloadConfirmModal from "./DownloadConfirmModal";
import UploadArea from "../../partials/UploadArea";
import UploadModal from "../../partials/UploadModal";

import LoginModal from "../users/LoginModal";
import WebsiteModal from "../sites/WebsiteModal";

import db from '../../../basics/db';

import * as utils from "../../../basics/utils";
import * as cordova from "../../../basics/cordova";
import * as appActions from "../../../store/actions/appActions";
import * as userActions from "../../../store/actions/userActions";
import * as iconActions from "../../../store/actions/iconActions";
import * as syncActions from "../../../store/actions/syncActions";
import * as fileActions from "../../../store/actions/fileActions";
import * as versionActions from "../../../store/actions/versionActions";
import * as settingActions from "../../../store/actions/settingActions";
import * as portfolioActions from "../../../store/actions/portfolioActions";
import * as containerActions from "../../../store/actions/containerActions";

import defaultBackground from '../../../assets/images/defaultBackground.jpg';

class ContainersController extends React.Component {
    constructor(props) {
        super(props);

        this.component = 'ContainersController';

        this.interval = null;

        this.timeout = null;

        this.intervalOnline = null
    }

    // ==========================================================================
    //
    // LIFESYCLE - SECTION
    //
    // ==========================================================================

    componentDidMount = async () => {
        const { checkContainersInterval, currentContainer, apiURL, online } = this.props.app;
        const { syncType } = this.props.sync;

        const debug = false;

        debug && console.log(`\n\n##################################################`);
        debug && console.log(`${this.component}:Mount(${currentContainer})`);
        debug && console.log(`##################################################\n\n`);

        this.props.closeModalDialog(); // appAction.js

        this.props.setToolbarType('containers'); // appAction.js

        // = LOAD DOWNLOAD LIST =====================================================
        // 
        // ==========================================================================
        this.loadDownloadList();

        // = LOAD CONTAINER LIST ====================================================
        // 
        // ==========================================================================
        this.loadContainerList();

        // ==========================================================================
        // 
        // Zyklisch prüfen, ob Kataloge/Containers auf dem Server aktualisiert wurden.
        // 
        // ==========================================================================
        let checkInterval = checkContainersInterval;
        if (process.env.NODE_ENV === 'development') {
            checkInterval = 5; // 5 Sekunden
        }

        if (checkInterval > 0) {

            const checkContainer = () => {
                // nicht pollen, wenn synchronisation läuft
                if (syncType !== '') return;

                if (!online) return

                this.loadContainerListFromAPI();
            }

            checkContainer(); // einmal bei start Container-Liste abholen

            setTimeout(() => checkContainer(), 3000); // etwas später nochmal...

            // und dann zyklisch Container-Liste abholen 
            this.interval = setInterval(() => checkContainer(), checkInterval * 1000);
        }

        const checkOnline = () => {

            axios({
                url: encodeURI(`${apiURL}/api/v1/containers/alive`),
                method: 'GET',
                responseType: 'json',
            }).then((ret) => {
                //console.log('checkOnline(true)', ret)
                this.props.setOnline(true);
            }).catch((err) => {
                console.log('checkOnline(false) err:', err)
                this.props.setOnline(false);
            })
        }

        this.intervalOnline = setInterval(() => checkOnline(), 3000);
    }

    componentDidUpdate(prevProps) {
        const { apiURL } = this.props.app;

        const debug = false;

        debug && console.log(`\n\n##################################################`);
        debug && console.log(`${this.component}:DidUpdate`);
        debug && console.log(`##################################################\n\n`);

        /**
         * #userData #vhost
         * In der iPad-Version wird der virtuelle Host aus der Login 
         * E-Mail-Adresse. Aus diesem Grund muss der Benutzer sich zwingend 
         * anmelden, wenn die globale Variable 'apiURL' noch leer ist.
         * Wenn der Benutzer sich angemeldet hat und dabei über den virtuellen Host
         * der Link zum RestAPI in der Variablen 'apiURL' verändert wird, dann 
         * müssen unmittelbar danach die Katalogdaten aktualisiert werden.
         */
        if (window['cordova'] && apiURL !== prevProps.app.apiURL) {
            this.loadContainerListFromAPI();
        }
    }

    componentWillUnmount = () => {
        const debug = false;

        debug && console.log(`\n\n##################################################`);
        debug && console.log(`${this.component}:Unmount`);
        debug && console.log(`##################################################\n\n`);

        if (this.interval) clearInterval(this.interval);

        if (this.intervalOnline) clearInterval(this.intervalOnline)
    }

    // ==========================================================================
    //
    // CONTAINER - SECTION
    //
    // ==========================================================================

    /**
     * containers.json aus IndexedDB lesen
     * 
     * Diese Funktion sorgt dafür, dass alle Kataloge/Containers zunächst aus der Datei 
     * 'container.json' aus der IndexedDB geladen werden. Falls diese dort noch nicht 
     * existieren, dann werden die Katalogdaten direkt vom API geladen.
     * Nach efogreichem Laden werden die Katalogdaten im flüchtigen REDUX-Store gesichert.
     * 
     */
    loadContainerList = async () => {
        const debug = false;

        debug && console.log(`\n\n##################################################`);
        debug && console.log(`ContainersController:loadContainerList()`);
        debug && console.log(`##################################################\n\n`);

        debug && console.log('containers.json laden');

        await cordova.readLocalJSON(null, 'containers.json').then((containersJSON) => {
            debug && console.log('   readLocalJSON(containers.json) success:', containersJSON);
            this.props.setContainerList(containersJSON); // appActions.js
        }).catch(async (error) => {
            debug && console.log('   ContainersController: containers.json existiert noch nicht!', error);
            debug && console.log('   ContainersController: Container-Liste wird vom API geladen!', error);

            this.loadContainerListFromAPI();
        });

        // = READY =================================================================
        // Erst hier ist die Anwendung fertig geladen
        // =========================================================================
        this.props.setContainersReady(true);
    }

    /**
     * containers.json vom API laden
     * 
     * Diese Funktion sorgt dafür, dass die Katalogdaten vom API geladen werden.
     * Nach erfolgreichem laden vom API wird der Inhalt in die Datei 'container.json'
     * in der IndexedDB gespeichert.
     * 
     * Die Funktion wird zyklisch aufgerufen, damit der Benutzer Änderungen in den 
     * Katalogen mitbekommt.
     * 
     * Dadurch, dass diese Funktion zyklisch aufgerufen wird, wird gleichzeitig auch
     * geprüft, ob eine Verbindung zum Server bzw. RestAPI besteht und das globale Flag
     * 'online' wird entsprechend mit true/false belegt. 
     */
    loadContainerListFromAPI = () => {
        const { apiURL } = this.props.app;

        const debug = false;

        /**
         * #userData #vhost
         * In der iPad-Version wird der virtuelle Host aus der E-Mail-Adresse
         * die beim Login angegeben wird abgeleitet. Aus diesem Grund muss 
         * der Benutzer sich zwingend Anmelden, wenn die globale Variable 
         * 'apiURL' noch leer ist. Die 'apiURL' enthält die URL zum RestAPI 
         * auf dem entsprechenden virtuellen Host.
         * 
         * Solange der Benutzer sich noch nicht angemeldet hat, können auch keine
         * aktuellen Katalog-Daten vom virtuellen Host geleaden werden.
         */
        if (window['cordova'] && !apiURL) {
            debug && console.log('Benutzer hat sich noch nicht eingeloggt!');
            return;
        }

        // Katalogdaten vom API laden
        this.props.getContainerListPromise().then((containers) => {
            utils.logHeap('get containerList from API');

            // Katalogdaten in IndexedDB sichern
            cordova.writeLocalJSON(null, 'containers.json', containers)
                .then((data) => {
                    this.props.setContainerList(containers); // appActions.js
                    debug && console.log('writeLocalJSON(container.json) success:', data);
                })
                .catch((error) => {
                    console.log('writeLocalJSON(container.json) error:', error);
                });
        }).catch((error) => {
            console.log('writeLocalJSON(container.json) error:', error);
        });
    }

    // ==========================================================================
    //
    // DOWNLOADS - SECTION
    //
    // ==========================================================================

    /**
     * downloads.json aus IndexedDB lesen
     *
     * Mit dieser Funktion werden alle bereits heruntergeladenen Container
     * sowohl in den nichtflüchtigen Speicher als auch in den flüchtigen REDUX-Store
     * übernommen werden. 
     */
    loadDownloadList = async () => {
        const debug = false;

        debug && console.log(`\n\n##################################################`);
        debug && console.log(`ContainersController:loadDownloadList()`);
        debug && console.log(`##################################################\n\n`);

        debug && console.log('downloads.json laden');

        await cordova.readLocalJSON(null, 'downloads.json').then((newDownloadList) => {
            debug && console.log('   readLocalJSON(downloads.json) success:', newDownloadList);
            this.props.setDownloadList(newDownloadList); // appActions.js
        }).catch(async (error) => {
            debug && console.log('   ContainersController: downloads.json existiert noch nicht!', error);

            let newDownloadList = { id: 'downloads.json', name: 'downloads.json', container: null, data: [] }

            await cordova.writeLocalJSON(null, 'downloads.json', newDownloadList)
                .then((data) => {
                    debug && console.log('   writeLocalJSON(downloads.json) success:', data);
                    this.props.setDownloadList(newDownloadList); // appActions.js
                })
                .catch((error) => {
                    debug && console.log('   writeLocalJSON(downloads.json) error:', error);
                });
        });
    }

    /**
     * Aktuelle Daten eines Containers in der IndexedDB löschen
     */
    deleteDownload = async (sContainer) => {
        const debug = false;

        const { downloadList } = this.props.container;

        let newData = [];
        downloadList.data.forEach((d) => {
            if (d.id.localeCompare(sContainer) !== 0) {
                newData.push(d);
            }
        })

        const newDownloadList = { id: 'downloads.json', name: 'downloads.json', container: null, data: newData }

        await cordova.writeLocalJSON(null, 'downloads.json', newDownloadList)
            .then((data) => {
                this.props.setDownloadList(newDownloadList)
                debug && console.log('   writeLocalJSON(downloads.json) success:', data);
            })
            .catch((error) => {
                debug && console.log('   writeLocalJSON(downloads.json) error:', error);
            })

        debug && console.log('DELETE PDF-Files');
        utils.logHeap('STEP 1');
        await db.pdf.where('container').equals(sContainer).delete();
        debug && console.log('DELETE JSON-Files');
        utils.logHeap('STEP 2');
        await db.json.where('container').equals(sContainer).delete();
        debug && console.log('DELETE IMAGE-Files');
        utils.logHeap('STEP 3');
        await db.image.where('container').equals(sContainer).delete();
        //debug && console.log('DELETE VIDEO-Files');
        //utils.logHeap('STEP 4');
        //await db.video.where('container').equals(sContainer).delete();
        debug && console.log('DELETE Portfolios');
        utils.logHeap('STEP 5');
        await db.portfolio.where('container').equals(sContainer).delete();
        utils.logHeap('STEP 6');
    }

    // ==========================================================================
    //
    // RENDER - SECTION
    //
    // ==========================================================================

    render() {
        const { inputHeight, windowWidth, windowHeight, unitHeight, orientation, currentContainer, modalDialog, containersReady, apiURL, coverEdit, online } = this.props.app;
        const { containerList } = this.props.container;
        const { loginUser } = this.props.user;
        const { progress, syncType } = this.props.sync;

        const debug = false;

        // ==========================================================================
        // 
        // GRID DIMENSIONEN
        // 
        // ==========================================================================

        let gridCols = orientation === 'landscape' ? "grid-cols-3" : "grid-cols-2"

        let gridWidth = windowWidth / 2.85;

        if (orientation === 'landscape') {
            gridWidth = windowWidth / 4.22;
            gridWidth = Math.min(gridWidth, 400)
        }

        let gridHeight = Math.min(windowHeight / 1.5, gridWidth * 2.5);



        // ==========================================================================
        // 
        // SUB RENDER
        // 
        // ==========================================================================

        /**
         * Kataloge in einzelnen Karten anzeigen
         */
        const renderContainerView = () => {
            if (syncType !== '') return null;

            const renderCatalog = (d, i) => {

                const containerDownload = utils.getDownload(d.id);

                const selected = currentContainer.localeCompare(d.id) === 0;

                const lastChange = d.settings.releaseNotes.timestamp;
                const currDownload = containerDownload ? containerDownload.settings.releaseNotes.timestamp : null;
                const bUpdateNeeded = containerDownload ? lastChange !== currDownload : false;

                const onSelect = (d) => {

                    if (containerDownload) {
                        this.props.resetContainer(); // containerAction.js

                        this.props.setAppPreview(true); // Preview immer einschalten

                        this.props.setCurrentContainer(d.id); // appAction.js
                        this.props.setCurrentContainerTitle(d.title); // appAction.js

                        this.props.setToolbarType('portfolios');

                        this.props.history.push('/portfolios');
                    } else {
                        debug && console.log('Katalog wurde noch nicht heruntergeladen!');
                    }
                }

                const onStartDownload = () => {
                    this.props.setCoverEdit(false);
                    if (!loginUser) {
                        this.props.setModalDialog('ConfirmModal', 'needLogin'); // appAction.js
                        return;
                    }

                    this.props.setCurrentContainer(d.id); // appAction.js

                    this.props.setModalDialog('DownloadConfirmModal', null, { container: d.id, version: 'live' }); // appAction.js
                }

                const renderCatalogHeader = (d) => {

                    const downloadSVG = () => {
                        const height = unitHeight * .5;
                        const width = height;

                        const color = 'white';

                        /*                            <svg width={width} height={height} viewBox="0 0 32 32">
                        <path fill={color} d="M19,13h9V4h-2v5.404C23.854,6.154,20.179,4,16,4C9.383,4,4,9.383,4,16s5.383,12,12,12
                            c6.279,0,11.489-4.85,12-11h-2c-0.503,5.046-4.823,9-10,9c-5.514,0-10-4.486-10-10S10.486,6,16,6c3.691,0,6.913,2.016,8.645,5H19
                            V13z" />
                        </svg>
                        */


                        return (
                            <svg width={width} height={height} viewBox="0 0 32 32">
                                <g fill={color}>
                                    <path d="M16,27C9.935,27,5,22.065,5,16c0-2.892,1.132-5.516,2.964-7.481l0.264,2.4l1.988-0.219L9.589,5L3.646,5.654l0.219,1.988,l2.448-0.27C4.26,9.669,3,12.685,3,16c0,7.168,5.832,13,13,13c1.615,0,3.155-0.309,4.582-0.85l-0.226-2.054,C19.019,26.675,17.547,27,16,27z" />
                                    <path d="M25.745,24.577C27.766,22.287,29,19.288,29,16c0-7.168-5.832-13-13-13c-1.589,0-3.106,0.301-4.515,0.825l0.225,2.047,C13.029,5.311,14.479,5,16,5c6.065,0,11,4.935,11,11c0,2.857-1.109,5.449-2.902,7.405l-0.261-2.375l-1.988,0.219l0.627,5.7,l5.943-0.654L28.2,24.307L25.745,24.577z" />
                                </g>
                            </svg>
                        )
                    }

                    const renderCatalogIcon = () => {

                        const style = {
                            width: gridWidth * .3,
                            height: gridWidth * .3,
                        }

                        const styleImage = {
                            opacity: containerDownload ? 1.0 : 0.3,
                            ...utils.getStyleBackgroundImageBase64(d.profileImage),
                        }
                        return (
                            <div className="flex items-start justify-start cursor-pointer" onClick={() => onSelect(d)}>
                                <div style={style}>
                                    <div className="no-underline hover:no-underline w-full flex flex-col" style={styleImage}></div>
                                </div>
                            </div>
                        )
                    }

                    const renderBadge = () => {
                        // roter Badge am sync-Button anzeigen, wenn auf dem Server eine neue Version vorliegt
                        if (!bUpdateNeeded) return null;

                        const style = {
                            width: (unitHeight * .15) + 'px',
                            marginTop: (unitHeight * .175) + 'px',
                            marginRight: (unitHeight * .17) + 'px',
                        };

                        const styleBadge = Object.assign(style, utils.getStyleHeight(unitHeight, .15, .15, .12));

                        return (
                            <div style={styleBadge} className="absolute bg-white rounded-full text-white"></div>
                        )
                    }

                    // ###############################################################
                    // renderCatalogHeader
                    // ###############################################################   
                    const style = {
                        height: unitHeight * 1.5,
                        minHeight: unitHeight * 1.5,
                        padding: unitHeight * 0.1,
                    }

                    const styleDownload = {
                        opacity: !containerDownload || bUpdateNeeded ? 1.0 : 0.3,
                    }

                    return (
                        <div className="w-full h-full flex flex-row" style={style}>
                            <div className="w-5/6 h-full flex flex-col items-start justify-top opacity-50" >
                                {renderCatalogIcon()}
                            </div>
                            {online &&
                                <div className="w-1/6 h-full flex flex-col items-end justify-top text-white" style={styleDownload} onClick={onStartDownload}>
                                    {downloadSVG()}
                                    {renderBadge()}
                                </div>
                            }
                        </div>
                    )
                }

                const renderCatalogTitle = (d) => {

                    // ###############################################################
                    // renderTitle / Katalog-Titel anzeigen
                    // ---------------------------------------------------------------
                    // Schöler
                    // 
                    //
                    // ###############################################################
                    const renderTitle = () => {

                        const style = utils.getStyleHeight(unitHeight, 0.5, 0.5, .45);

                        return (
                            <div className="w-full flex flex-col font-bold overflow-hidden justify-top" style={style}>
                                <p>{d.title}</p>
                            </div>
                        )
                    }

                    // ###############################################################
                    // renderSubTitle / Katalog-Titel anzeigen
                    // ---------------------------------------------------------------
                    // Industriemaschinen
                    // 
                    //
                    // ###############################################################
                    const renderSubTitle = () => {

                        const style = utils.getStyleHeight(unitHeight, 0.25, .25, .20);

                        return (
                            <div className="w-full flex flex-col font-thin overflow-hidden justify-top" style={style}>
                                <p>{d.subTitle}</p>
                            </div>
                        )
                    }

                    // ###############################################################
                    // renderCatalogHeader
                    // ###############################################################   
                    const style = {
                        opacity: containerDownload ? 1.0 : 0.3,
                        padding: unitHeight * 0.1,
                    }

                    return (
                        <div className="w-full flex flex-row cursor-pointer" style={style} onClick={() => onSelect(d)}>
                            <div className="w-full flex flex-col pt-4">
                                {renderTitle()}
                                {renderSubTitle()}
                            </div>
                        </div>
                    )
                }

                const renderCatalogImage = (d) => {

                    const styleInner = {
                        width: gridWidth * .66,
                        height: gridWidth * .66,
                    }

                    const styleOuter = {
                        paddingTop: unitHeight * 0.3,
                    }

                    if (coverEdit && selected) {
                        const styleImage = {
                            ...utils.getStyleHeight(unitHeight, 0.1, 0.1, 0.2),
                            height: '100%',
                        }

                        return (
                            <div className="w-full h-full flex flex-col" style={styleImage}>
                                <UploadArea uploadArea='image' uploadType='common' uploadPath={''} uploadPost='' />
                            </div>)

                    } else {
                        const styleImage = {
                            opacity: containerDownload ? 1.0 : 0.3,
                            ...utils.getStyleBackgroundImageBase64('data:image/png;base64,' + d.base64),
                        }
                        return (
                            <div style={styleOuter} className="flex items-end justify-center cursor-pointer" onClick={() => onSelect(d)}>
                                <div style={styleInner}>
                                    <div className="no-underline hover:no-underline w-full flex flex-col rounded-full border-white border-4" style={styleImage}></div>
                                </div>
                            </div>
                        )
                    }
                }

                const renderCatalogFooter = (d) => {

                    if (!online) return null;

                    // ###############################################################
                    // renderSubTitleUpper / Status des Containers anzeigen
                    // ---------------------------------------------------------------
                    // Zuletzt geändert: [Datum/Uhrzeit]
                    //
                    // ###############################################################
                    const renderSubTitleUpper = () => {

                        const style = {
                            ...utils.getStyleHeight(unitHeight, .2, .2, .11),
                        }

                        const title = bUpdateNeeded ? `Zuletzt geändert: ${lastChange}` : '';

                        return (
                            <div className="w-full flex flex-col whitespace-no-wrap overflow-hidden" style={style}>{title}</div>
                        )
                    }

                    // ###############################################################
                    // renderSubTitleUpper / Letzte Aktualisiertung anzeigen
                    // ---------------------------------------------------------------
                    // 
                    // Letzter Download: [Datum/Uhrzeit]
                    // ###############################################################
                    const renderSubTitleLower = () => {

                        let title = '';
                        if (containerDownload) title = 'Katalog ist auf dem neuesten Stand.';
                        if (bUpdateNeeded) title = `Letzter Download: ${currDownload}`;

                        const style = {
                            ...utils.getStyleHeight(unitHeight, .2, .2, .11),
                        }

                        return (
                            <div className="w-full whitespace-no-wrap overflow-hidden" style={style}>{title}</div>
                        )
                    }

                    // ###############################################################
                    // renderCatalogHeader
                    // ###############################################################   
                    const style = {
                        opacity: bUpdateNeeded ? 1.0 : 0.3
                    }

                    return (
                        <div className="relative w-full h-full flex flex-row cursor-pointer" style={style} onClick={() => onSelect(d)}>
                            <div className="absolute bottom-2 w-full px-4">
                                {renderSubTitleUpper()}
                                {renderSubTitleLower()}
                            </div>
                        </div>
                    )
                }

                const renderCatalogToolbar = (d) => {

                    if (!online) return null;

                    const getEditCatalogPermission = () => {
                        if (!loginUser) return false;

                        /*
                        console.log('getEditCatalogPermission()', d);
                        console.log('loginUser:', loginUser);
                        console.log('containerDownload.catalog.company:', d.catalog.company);
                        console.log('containerDownload.company:', d.company);
                        */

                        // Ein 'Supperuser' kann alle Kataloge bearbeiten
                        if (["Superadmin"].includes(loginUser._role)) return true;

                        // Ein 'Admin' oder 'Moderator' kann nur die unternehmseigenen Kataloge bearbeiten
                        if (["Admin", "Moderator"].includes(loginUser.role)) {
                            if (d.catalog.company.name === d.company.name) {
                                return true;
                            }
                        }
                        // Alle anderen können Kataloge nicht bearbeiten
                        return false;
                    }

                    const renderDummyIcon = (index) => {
                        return <FooterItem index={index} link={null} type={null} label={''} callback={null} />
                    }

                    const renderVersionsIcon = (index) => {
                        if (!getEditCatalogPermission()) return renderDummyIcon(index);

                        const onClick = () => {
                            this.props.setCoverEdit(false);
                            if (!loginUser) {
                                this.props.setModalDialog('ConfirmModal', 'needLogin'); // appAction.js
                                return;
                            }
                            this.props.setPath(''); // appAction.js

                            this.props.setToolbarType('versions') // appAction.js

                            this.props.setCurrentContainer(d.id); // appAction.js
                        }

                        return <FooterItemCatalog index={index} link={'/versions'} type={'versions'} label={'Archiv'} callback={onClick} />
                    }

                    const renderEditIcon = (index) => {
                        if (!getEditCatalogPermission()) return renderDummyIcon(index);

                        const onClick = () => {
                            this.props.setCoverEdit(false);
                            if (containerDownload) {
                                this.props.resetContainer(); // containerAction.js

                                this.props.setAppPreview(false); // Preview immer einschalten

                                this.props.setCurrentContainer(d.id); // appAction.js

                                this.props.setToolbarType('portfolios');

                                this.props.history.push('/portfolios');
                            } else {
                                debug && console.log('Katalog wurde noch nicht heruntergeladen!');
                            }
                        }
                        return <FooterItemCatalog index={index} link={null} type={'edit2'} label={'Bearbeiten'} callback={onClick} />
                    }

                    const renderPublishIcon = (index) => {
                        if (!getEditCatalogPermission()) return renderDummyIcon(index);

                        const onClick = () => {
                            this.props.setCoverEdit(false);
                            this.props.setModalDialog('ConfirmModal', 'confirmGoLive');
                        };

                        return <FooterItemCatalog index={index} link={null} type={'upload'} label={'Publish'} callback={onClick} />
                    }

                    const renderDownloadIcon = (index) => {
                        const onDeleteDownload = () => {
                            this.props.setCoverEdit(false);
                            this.props.setModalDialog('ConfirmModal', 'deleteDownload', { container: d.id });
                        }

                        if (containerDownload) {
                            return <FooterItemCatalog index={index} link={null} type={'close'} label={'Löschen'} callback={onDeleteDownload} />
                        } else {
                            return <FooterItemCatalog index={index} link={null} type={'download'} label={'Download'} callback={onStartDownload} />
                        }
                    }

                    const renderCoverEditIcon = (index) => {
                        if (!getEditCatalogPermission()) return renderDummyIcon(index);

                        const onClick = () => {
                            this.props.setCurrentContainer(d.id);
                            this.props.setCoverEdit(!coverEdit);
                        }

                        return <FooterItemCatalog index={index} link={null} type={'edit'} label={'Cover'} callback={onClick} />
                    }

                    const style = {
                        height: unitHeight, backgroundColor: (debug) ? 'yellow' : 'transparent'
                    }

                    return (
                        <div className="w-full h-full flex flex-row items-start pt-2" style={style}>
                            {renderEditIcon(0)}
                            {renderPublishIcon(1)}
                            {renderDummyIcon(2)}
                            {renderCoverEditIcon(3)}
                            {renderVersionsIcon(4)}
                            {renderDownloadIcon(5)}
                        </div>
                    )
                }

                // ###############################################################
                // renderCatalog
                // ###############################################################

                const style = {
                    width: gridWidth,
                    minWidth: gridWidth,
                    height: gridHeight,
                    minHeight: gridHeight,
                }

                let styleBG = { backgroundColor: d.bgColor }

                return (
                    <div key={i} className={`flex flex-col h-full`} style={style}>
                        <div className={`flex h-full flex-col rounded-xl text-white px-2`} style={styleBG}>
                            {renderCatalogHeader(d)}
                            {renderCatalogTitle(d)}
                            {renderCatalogImage(d)}
                            {renderCatalogFooter(d)}
                        </div>
                        <div className={`flex flex-col mt-2`} >
                            {renderCatalogToolbar(d)}
                        </div>
                    </div>
                )
            }

            // ###############################################################
            // renderContainerView
            // ###############################################################   

            const style = {
                height: inputHeight,
                minHeight: inputHeight,
                backgroundPosition: 'center center',
                backgroundImage: "url(" + defaultBackground + ")",
            }

            return (
                <div className="flex h-full w-full items-center justify-center pt-16 overflow-y-auto" style={style}>
                    <div className="flex h-full w-3/4 items-center">
                        <div className={`h-full grid gap-8 ${gridCols}`}>
                            {containerList.map((d, i) => renderCatalog(d, i))}
                        </div>
                    </div>
                </div>
            )
        }

        /**
         * Modaldialog
         * +---------------------------------------------------+
         * | Anmeldung erforderlich!                           |
         * +---------------------------------------------------+
         * |                                            [ OK ] |
         * +---------------------------------------------------+
         */
        const renderNeedLogin = () => {

            const onConfirm = () => {
                this.props.setModalDialog('LoginModal', ''); // appAction.js
            }

            const onCancel = () => {
                this.props.closeModalDialog();
            }

            return (
                <ConfirmModal dialog='ConfirmModal' type='needLogin' title="Anmeldung erforderlich" onConfirm={onConfirm} onCancel={onCancel}>
                    <div className="flex justify-start text-2xl font-bold">Sie müssen angemeldet sein, um dies Aktion auszuführen!</div>
                </ConfirmModal>
            )
        }

        /**
         * Modaldialog um das Löschen zu bestätigen
         * +---------------------------------------------------+
         * | Katalog löschen?                                  |
         * +---------------------------------------------------+
         * | [ABBRUCH]                                  [ OK ] |
         * +---------------------------------------------------+
         */
        const renderDeleteConfirm = () => {
            if (!modalDialog.details) return null;

            const sContainer = modalDialog.details.container;

            const onConfirm = () => {
                this.deleteDownload(sContainer);
                this.props.closeModalDialog();
            }

            const onCancel = () => {
                this.props.closeModalDialog();
            }

            return (
                <ConfirmModal dialog='ConfirmModal' type='deleteDownload' title="Bestätigung" onConfirm={onConfirm} onCancel={onCancel}>
                    <div className="flex justify-start text-2xl font-bold whitespace-nowrap">Katalog löschen?</div>
                    <br />
                    <div className="flex justify-start">Durch das Löschen wird der Katalog aus dem lokalen Speicher entfernt, sodass dieser nicht mehr Offline angezeigt werden kann.</div>
                </ConfirmModal>
            )
        }

        // ==========================================================================
        // 
        // MAIN RENDER
        //                                                                           
        // ==========================================================================

        if (!containersReady) { return null }

        if (window['cordova'] && !containerList.length && !apiURL) {
            console.log(this.component + ': !apiURL -> redirect to /login\n\n`');
            return <Redirect to='/login' />
        }

        return (
            <div className="ContainersController w-full h-full flex flex-col">
                <Header />
                {renderContainerView()}
                {renderDeleteConfirm()}
                {renderNeedLogin()}
                <ProgressView />
                <ProgressBar />
                <DownloadConfirmModal />
                <LoginModal />
                <WebsiteModal />
                <UploadModal />
                {(syncType !== '' && progress !== null) ? null : <Footer />}
            </div>)


    }
}

function mapStateToProps(state) {
    return {
        app: state.app,
        user: state.user,
        icon: state.icon,
        file: state.file,
        sync: state.sync,
        version: state.version,
        settings: state.settings,
        portfolio: state.portfolio,
        container: state.container
    }
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(Object.assign({}, appActions, userActions, iconActions, syncActions, fileActions, versionActions, containerActions, portfolioActions, settingActions), dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(ContainersController);